/** Verified: docs/operations/clause-verifications/compliance-only-duties-2026-09-30.md. */
/**
 * Governance register clocks: Conflict of Interest renewal, governing-body
 * skills currency, and risk register review. None of the three appear in
 * the constants register with a verified numeric statutory interval —
 * all TARGET, a Statura-configured cadence.
 */
import { DEFAULT_TZ, dateOnlyInTz } from '../timezone';
import type { ComplianceDeadlineItem } from './types';
import { nextQcabReportDue } from '../compliance-packs/qcab';
import type { ComplianceDeadlineSettings } from '../compliance-settings';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
function remainingDays(value: string, now: Date, timezone: string): number {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value))
    return (
      (Date.parse(`${value}T00:00:00Z`) - Date.parse(`${dateOnlyInTz(now, timezone)}T00:00:00Z`)) /
      MS_PER_DAY
    );
  return (Date.parse(value) - now.getTime()) / MS_PER_DAY;
}

export interface ConflictOfInterestRow {
  id: string;
  expiresAt: string;
}

const COI_PROVISION =
  'Statura operational target (annual conflict-of-interest re-attestation cadence)';

/** Approaching within 30 days of expiry. */
export function evaluateConflictOfInterestRenewal(
  row: ConflictOfInterestRow,
  now: Date,
  settings?: ComplianceDeadlineSettings,
  timezone: string = DEFAULT_TZ,
): { item: ComplianceDeadlineItem } | null {
  const warningLeadDays = settings?.conflict_of_interest_warning_lead ?? 30;
  const daysUntil = remainingDays(row.expiresAt, now, timezone);
  if (!Number.isFinite(daysUntil) || daysUntil > warningLeadDays) return null;
  return {
    item: {
      id: `coi-renewal:${row.id}`,
      register: 'CONFLICT_OF_INTEREST',
      recordRef: row.id,
      title: 'Conflict of interest declaration renewal',
      dueAt: row.expiresAt,
      kind: 'TARGET',
      provisionOrTargetLabel: COI_PROVISION,
      status: daysUntil < 0 ? 'OVERDUE' : 'DUE_SOON',
      href: '/compliance/governance/conflicts',
      meta: { daysUntil },
    },
  };
}

export interface GoverningBodySkillRow {
  id: string;
  expiresAt: string;
  skillCategory: string | null;
}

const SKILLS_PROVISION = 'Statura operational target (governing-body skills currency cadence)';

/** Approaching within 30 days of expiry. */
export function evaluateGoverningBodySkillsExpiry(
  row: GoverningBodySkillRow,
  now: Date,
  settings?: ComplianceDeadlineSettings,
  timezone: string = DEFAULT_TZ,
): { item: ComplianceDeadlineItem } | null {
  const warningLeadDays = settings?.governing_body_skills_warning_lead ?? 30;
  const daysUntil = remainingDays(row.expiresAt, now, timezone);
  if (!Number.isFinite(daysUntil) || daysUntil > warningLeadDays) return null;
  return {
    item: {
      id: `governing-body-skill:${row.id}`,
      register: 'GOVERNING_BODY_SKILLS',
      recordRef: row.id,
      title: `Governing-body skill currency expiring${row.skillCategory ? ` (${row.skillCategory})` : ''}`,
      dueAt: row.expiresAt,
      kind: 'TARGET',
      provisionOrTargetLabel: SKILLS_PROVISION,
      status: daysUntil < 0 ? 'OVERDUE' : 'DUE_SOON',
      href: '/compliance/governance/skills-matrix',
      meta: { daysUntil },
    },
  };
}

export interface RiskReviewRow {
  id: string;
  riskTitle: string | null;
  reviewDate: string;
}

const RISK_PROVISION = 'Statura operational target (provider-specified risk review cadence)';

/** Approaching within 14 days of the risk register's own review date. */
export function evaluateRiskReviewDue(
  row: RiskReviewRow,
  now: Date,
  settings?: ComplianceDeadlineSettings,
  timezone: string = DEFAULT_TZ,
): { item: ComplianceDeadlineItem } | null {
  const warningLeadDays = settings?.risk_review_warning_lead ?? 14;
  const daysUntil = remainingDays(row.reviewDate, now, timezone);
  if (!Number.isFinite(daysUntil) || daysUntil > warningLeadDays) return null;
  return {
    item: {
      id: `risk-review:${row.id}`,
      register: 'RISK_REGISTER',
      recordRef: row.id,
      title: 'Risk register review due',
      dueAt: row.reviewDate,
      kind: 'TARGET',
      provisionOrTargetLabel: RISK_PROVISION,
      status: daysUntil < 0 ? 'OVERDUE' : 'DUE_SOON',
      href: '/compliance/governance/risk-register',
      meta: { daysUntil },
    },
  };
}

export interface QcabReportDueRow {
  bodyId: string;
  lastReportPeriodEnd: string;
  tightenToMonths?: number;
}

const QCAB_PROVISION =
  'Aged Care Act 2024 s 158(2); Aged Care Rules 2025 rr 158-5, 158-10, 158-15; r 154-800';

/**
 * The calendar derives one open report item from the latest completed period.
 * A provider target can be tighter, but never later than the six-month duty.
 */
export function evaluateQcabReportDue(
  row: QcabReportDueRow,
  now: Date,
  timezone: string = DEFAULT_TZ,
): { item: ComplianceDeadlineItem } | null {
  const dueAt = nextQcabReportDue(row.lastReportPeriodEnd, row.tightenToMonths);
  const daysUntil =
    (Date.parse(`${dueAt}T00:00:00Z`) - Date.parse(`${dateOnlyInTz(now, timezone)}T00:00:00Z`)) /
    MS_PER_DAY;
  if (daysUntil > 30) return null;
  return {
    item: {
      id: `qcab-report:${row.bodyId}:${row.lastReportPeriodEnd}`,
      register: 'QCAB_REPORT',
      recordRef: row.bodyId,
      title: 'Quality Care Advisory Body report due',
      dueAt,
      kind: 'STATUTORY',
      provisionOrTargetLabel: QCAB_PROVISION,
      status: daysUntil < 0 ? 'OVERDUE' : 'DUE_SOON',
      href: '/compliance/governance/qcab',
      meta: { daysUntil, lastReportPeriodEnd: row.lastReportPeriodEnd },
    },
  };
}
